import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Users, ChevronDown, Eye, X } from "lucide-react";

const STORAGE_KEY = "impersonate_coach_id";

/**
 * Super-admin only. A topbar dropdown that lets the super-admin pick any coach
 * and view the app exactly as that coach would (their roster, groups, teams).
 *
 * The selected coach_id is written to localStorage and the axios interceptor
 * sends it on every request as `X-Impersonate-Coach-Id`. Changing it triggers
 * a full reload so all data refetches under the new scope.
 */
export default function WorkspaceSwitcher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!user || user.role !== "super_admin") return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [user]);

  // Only super-admin gets the switcher.
  if (!user || user.role !== "super_admin") return null;

  const ensureLoaded = async () => {
    if (workspaces.length > 0) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/workspaces");
      setWorkspaces(data || []);
    } finally { setLoading(false); }
  };

  const pick = (coachId) => {
    if (coachId) localStorage.setItem(STORAGE_KEY, coachId);
    else localStorage.removeItem(STORAGE_KEY);
    setActiveId(coachId);
    setOpen(false);
    // Reload so every cached query re-fetches under the new scope.
    window.location.reload();
  };

  const active = workspaces.find((w) => w.coach_id === activeId);
  const displayName = activeId
    ? (active?.name || "Impersonating…")
    : "All workspaces";

  return (
    <div ref={wrapRef} className="relative" data-testid="workspace-switcher">
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); ensureLoaded(); }}
        data-testid="workspace-switcher-toggle"
        className={`flex items-center gap-2 px-3 py-1.5 border rounded-md transition text-xs ${
          activeId
            ? "border-[#FF9F0A] bg-[#FF9F0A]/10 text-[#FF9F0A]"
            : "border-white/15 hover:border-white/40 text-[#A1A1AA] hover:text-white"
        }`}
      >
        {activeId ? <Eye size={12} /> : <Users size={12} />}
        <span className="uppercase tracking-widest font-bold">
          {activeId ? "Viewing:" : "Workspace:"}
        </span>
        <span className="normal-case font-normal tracking-normal max-w-[150px] truncate">
          {displayName}
        </span>
        <ChevronDown size={12} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-72 bg-[#0E0E10] border border-white/15 rounded-md shadow-2xl max-h-[70vh] overflow-y-auto"
             data-testid="workspace-switcher-menu">
          <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between bg-[#121214]">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] font-bold">// impersonate</span>
            {activeId && (
              <button onClick={() => pick("")} data-testid="workspace-exit-impersonation"
                      className="text-[10px] uppercase tracking-widest font-bold text-[#FF9F0A] hover:text-white flex items-center gap-1">
                <X size={10} /> Exit
              </button>
            )}
          </div>

          <button
            onClick={() => pick("")}
            data-testid="workspace-all"
            className={`w-full text-left px-3 py-2.5 flex items-center justify-between hover:bg-white/5 ${!activeId ? "bg-white/5" : ""}`}
          >
            <div>
              <div className="text-sm font-bold text-white">All Workspaces</div>
              <div className="text-[10px] text-[#71717A]">Super-admin view (see everything)</div>
            </div>
            {!activeId && <span className="text-[#FF3B30] text-xs">●</span>}
          </button>

          <div className="px-3 py-1.5 border-y border-white/10 bg-[#121214] text-[10px] uppercase tracking-[0.18em] text-[#71717A] font-bold">// coaches</div>

          {loading && <div className="px-3 py-4 text-xs text-[#71717A]">loading workspaces…</div>}

          {!loading && workspaces.length === 0 && (
            <div className="px-3 py-4 text-xs text-[#71717A]">No coaches yet.</div>
          )}

          {!loading && workspaces.map((w) => {
            const isActive = w.coach_id === activeId;
            return (
              <button
                key={w.coach_id}
                onClick={() => pick(w.coach_id)}
                data-testid={`workspace-pick-${w.coach_id}`}
                className={`w-full text-left px-3 py-2.5 flex items-center justify-between hover:bg-white/5 border-b border-white/5 last:border-b-0 ${
                  isActive ? "bg-[#FF9F0A]/10" : ""
                }`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-bold text-white truncate">{w.name}</div>
                  <div className="text-[10px] text-[#71717A] truncate">{w.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="text-[10px] tabular text-[#A1A1AA]">{w.athlete_count} athletes</span>
                  {w.status !== "active" && (
                    <span className="text-[9px] uppercase tracking-wider text-[#FF9F0A]">{w.status}</span>
                  )}
                  {isActive && <span className="text-[#FF9F0A] text-xs">●</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
